import React, { FC, useEffect, useRef, useState } from "react";

// Types
import { FormStateType } from "../Helpers/formTypes";
import { RootDataType } from "../Helpers/interfaces";

// Hooks
import { useLabels } from "../Hooks/useLabels";

// Components
import { Heading } from "../FormElements";
import { FormAppointmentDetails } from "../FormAppointmentDetails/FormAppointmentDetails";
import { PracticalInformation } from "../PracticalInformation/PracticalInformation";
import { CenterInformation } from "../CenterInformation/CenterInformation";
import { FAQAccordion } from "../FAQAccordion/FAQAccordion";
import { locationInitialState } from "../Helpers/initialState/locationInitialState";
import { WaitWhileSmsModal } from "../FormElements/PopUps/WaitWhileSmsModal";
import Modal from "react-modal";
import { GtmService } from "../GtmService";
import { calculateTotalPrice } from "../Helpers/totalPriceCalculator";
import { useBookingContext } from "../../BookingButton/BookingContext/BookingContext";
import { OptionalCampaign } from "../OptionalCampaign/OptionalCampaign";
import { useTrackEvent } from "@optimizely/react-sdk";

type FormFourthStepProps = {
  state: FormStateType;
  rootData: RootDataType;
};

export const FourthStep: FC<FormFourthStepProps> = ({ state, rootData }) => {
  const { bookingLabels } = useLabels();
  const fourthStepLabels = bookingLabels?.stepsBookingLabels.fourthStepLabels;
  const { waitlist, optimizelyExperimentation, trackAbEvent } =
    useBookingContext();
  const doctor = rootData.doctors.filter(
    (d) => d.id == state.appointment.doctorId
  )[0];
  const center = rootData.centers.filter(
    (d) => d.id == state.appointment.centerId
  )[0] || { ...locationInitialState, name: waitlist?.centerName };

  const isWaitlist = !!waitlist?.contentId;

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [showSmsModal, setShowSmsModal] = useState(true);

  const [track, clientReady, didTimeout] = useTrackEvent();

  useEffect(() => {
    if (
      trackAbEvent &&
      clientReady &&
      optimizelyExperimentation?.events.bookingCompleted
    ) {
      track(optimizelyExperimentation.events.bookingCompleted);
    }
  }, [clientReady]);

  useEffect(() => {
    Modal.setAppElement(wrapperRef.current!);

    GtmService.appointmentConfirmed(
      state.specialization.name,
      state.specialization.id,
      getService().bookingName,
      getService().id,
      center?.name,
      center?.id,
      doctor?.name,
      doctor?.id,
      state.appointment.startTime,
      state.bookingResponse.booking.applyForMembership,
      calculateTotalPrice(state.pricingSummary)
    );
  }, []);

  const getService = () => {
    return (
      rootData.services.find((s) => s.id === state.appointment.serviceId) ??
      state.service
    );
  };

  return (
    <div ref={wrapperRef} className="mb-4">
      <div className="mb-3">
        <Heading>
          {isWaitlist ? "Bekreftelse" : fourthStepLabels?.subHeading}
        </Heading>
        <FormAppointmentDetails
          doctor={doctor}
          center={center}
          service={getService()}
          specialization={state.specialization}
          appointment={state.appointment}
          pricingSummary={state.pricingSummary}
          cancellationMessage={state.cancellationDeadline?.message || ""}
          waitlist={waitlist}
          modal={Modal}
        />
        <OptionalCampaign
          service={state.service}
          center={center}
          optionalCampaign={fourthStepLabels?.optionalCampaign}
        />
        <PracticalInformation
          specialization={state.specialization}
          service={getService()}
          bookingResponse={state.bookingResponse}
          isWaitlist={!!waitlist?.contentId}
        />
        <CenterInformation
          centerName={isWaitlist ? waitlist.centerName : center.name}
          bookingResponse={state.bookingResponse}
        />
        <FAQAccordion localFAQ={state.bookingResponse.faq} />
      </div>

      <div className="d-flex">
        <div className="mx-auto">
          <a href="/" className="btn btn-primary">
            {fourthStepLabels?.buttonLabel}
          </a>
        </div>
      </div>
      {isWaitlist && (
        <WaitWhileSmsModal
          onClose={() => setShowSmsModal(false)}
          title={waitlist?.smsModal.header}
          description={waitlist?.smsModal.description}
          buttonText={waitlist?.smsModal.button.link.title}
          Modal={Modal}
          isOpen={showSmsModal}
        />
      )}
    </div>
  );
};
